import React, { useState, useMemo, useContext } from "react";
import { Tooltip, Tabs, Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

import TrialModal from "../../common/TrialModal";
import KeyExecutives from "./KeyExecutives";
import {
  getEmployeeList,
  getExecutiveTag,
  resetExecutiveTag,
  emptyErrorObj,
  downloadExecutiveExl,
} from "../../actionCreator/executiveDetailsActionCreator";
import { getUserInfo } from "../../utils/utils";
import { saveExcel, savePDF } from "../../utils";
import { useExport } from "../executive-search/hooks/useExport";
import { errEnum } from "../../config";
import TagExecutive from "../executive-search/TagExecutive";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import pdf from "../../assets/svg/pdf.svg";
import tag from "../../assets/svg/tag.svg";

const SummaryContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { isAuthenticated: isLoggedIn } = useContext(AuthContext);
  const { getSchema, columns } = useExport();
  const { id } = useParams();
  const [tabActiveKey, setTabActiveKey] = useState("1");
  const [openTagModal, setOpenTagModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const executiveCompanyDetails = useSelector(
    (state) => state.executiveDetailsReducer.executiveCompanyDetails,
  );

  const userAccountInfo = useSelector(
    (state) => state.CommonReducer.accountInfo,
  );

  const selectedEmployeeList = useSelector(
    (state) => state.executiveDetailsReducer.selectedColleague,
  );

  const executiveDetails = useSelector(
    (state) => state.executiveDetailsReducer.executiveDetails,
  );
  const employeeList = useSelector(
    (state) => state.executiveDetailsReducer.employeeList,
  );

  const errObj = useSelector((state) => state.executiveDetailsReducer.errObj);

  useMemo(() => {
    if (Object.keys(getUserInfo()).length && executiveCompanyDetails.length) {
      const { id } = getUserInfo();
      dispatch(resetExecutiveTag());
      const [executiveCompanyDetails1] = executiveCompanyDetails;
      dispatch(getExecutiveTag(executiveCompanyDetails1?.id, id));
    }
  }, [executiveCompanyDetails, userAccountInfo]);

  const items = [
    {
      key: "1",
      label: "Colleagues",
      children: <KeyExecutives />,
    },
  ];

  const onChange = (key) => {
    setTabActiveKey(key);
    if (key !== "2") {
      dispatch(getEmployeeList(id));
    }
  };

  const tagExecutive = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedEmployeeList?.length) {
        setOpenTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const closeTagModal = () => {
    setOpenTagModal(false);
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    dispatch(emptyErrorObj());
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const onExport = async (type) => {
    checkLoginStatus();
    if (isLoggedIn) {
      let selectedEmpIds = "";
      selectedEmployeeList?.forEach((exe) => {
        selectedEmpIds += `${exe?.id},`;
      });
      selectedEmpIds = selectedEmpIds.substring(
        selectedEmpIds.lastIndexOf(","),
        0,
      );
      const [executiveCompanyDetails1] = executiveCompanyDetails;
      const payload = {
        empIds: selectedEmpIds,
        companyId: executiveCompanyDetails1?.id,
        id: executiveDetails?.id,
      };
      const res = await dispatch(downloadExecutiveExl(payload, type));
      if (res?.length) {
        const downloadedUpdatedData = getSchema(res);
        const fileName = executiveCompanyDetails1.name + "-executive-data";
        if (type === "exl") {
          saveExcel(downloadedUpdatedData, columns, fileName);
        } else {
          savePDF(downloadedUpdatedData, columns, fileName);
        }
        showAlert(
          "success",
          "Download Complete",
          <div>
            The colleague data for {executiveDetails.fullname} has been
            successfully downloaded to{" "}
            {type === "exl" ? (
              <FileExcelOutlined className="tw-text-[#4CAF50]" />
            ) : (
              <FilePdfOutlined className="tw-text-[#f44336]" />
            )}
            .
          </div>,
        );
      }
    }
  };

  return (
    <>
      <div className="tw-flex tw-my-4 tw-justify-end tw-items-center tw-gap-2.5 tw-font-inter">
        {!!employeeList?.length && (
          <>
            <Tooltip title="Tag Executive">
              <div
                className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-size-10 tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 tw-bg-white tw-transition-colors"
                onClick={() => {
                  tagExecutive();
                }}
              >
                <img src={tag} alt="" className="tw-size-5 tw-cursor-pointer" />
              </div>
            </Tooltip>
            <Tooltip title="Download as Excel">
              <div
                className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-size-10 tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 tw-bg-white tw-transition-colors"
                onClick={() => onExport("exl")}
              >
                <i
                  className="right-icons la la-file-excel tw-text-lg tw-text-slate-500"
                  aria-hidden="true"
                ></i>
              </div>
            </Tooltip>
            <Tooltip title="Download as PDF">
              <div
                className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-size-10 tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 tw-bg-white tw-transition-colors"
                onClick={() => onExport("pdf")}
              >
                <img src={pdf} alt="" className="tw-size-5 tw-cursor-pointer" />
              </div>
            </Tooltip>
          </>
        )}
        <FeatureComingSoon>
          <Button className="tw-h-10 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 tw-text-xs tw-font-bold tw-text-slate-600 hover:tw-text-slate-800 tw-rounded-xl tw-px-4 tw-flex tw-items-center tw-gap-1.5">
            <i className="fa fa-plus" aria-hidden="true"></i> Create List
          </Button>
        </FeatureComingSoon>

        <FeatureComingSoon>
          <Button className="tw-h-10 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 tw-text-xs tw-font-bold tw-text-slate-600 hover:tw-text-slate-800 tw-rounded-xl tw-px-4 tw-flex tw-items-center tw-gap-1.5">
            <i className="fs-14 mr-1 fas fa-paper-plane"></i>Send Campaign
          </Button>
        </FeatureComingSoon>
      </div>
      <div id="executive" className="tw-mt-4 tw-mb-6">
        <Tabs
          activeKey={tabActiveKey}
          items={items}
          onChange={onChange}
          className="tw-border tw-border-solid !tw-border-slate-200/80 !tw-rounded-2xl tw-bg-white tw-shadow-sm tw-overflow-hidden"
        />
      </div>
      {errObj[errEnum.DOWNLOAD_ERROR] && (
        <TrialModal
          openModal={!!errObj[errEnum.DOWNLOAD_ERROR]}
          closeModal={() => dispatch(emptyErrorObj())}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.DOWNLOAD_ERROR]
                ? errObj[errEnum.DOWNLOAD_ERROR].message
                : "Unable to complete the action. Please try again later."}
            </div>
          }
          modalWidth="400px"
        />
      )}

      {showModal ? (
        <TrialModal
          openModal={showModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
        />
      ) : (
        ""
      )}

      <TagExecutive
        title="Tag Colleague"
        selectedRecords={selectedEmployeeList?.map((d) => d?.id)}
        showTagModal={openTagModal && !!selectedEmployeeList?.length}
        closeTagModal={closeTagModal}
      />
    </>
  );
};

export default SummaryContent;
